import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  Chip,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import TableChartIcon from '@mui/icons-material/TableChart';
import PrintIcon from '@mui/icons-material/Print';
import ClearIcon from '@mui/icons-material/Clear';
import TablePagination from '@mui/material/TablePagination';

interface LoanTransaction {
  loanId: string;
  borrower: string;
  bank: string;
  loanType: string;
  loanReference: string;
  signingDate: Date;
  availabilityExpiryDate: Date;
  hedgeCeilingAmount: number;
  approvedLimit: number;
  status: 'Active' | 'Closed' | 'Cancelled';
  rateOfInterest: number;
  gracePeriod: number;
  principalRepaymentFrequency: string;
  interestRepaymentFrequency: string;
  arrangementFee: number;
  creationDate: Date;
  lastUpdatedDate: Date;
  createdBy: string;
  lastUpdatedBy: string;
}

const LoanTransaction: React.FC = () => {
  const [loanTransactions, setLoanTransactions] = useState<LoanTransaction[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState<LoanTransaction | null>(null);
  const [dialogType, setDialogType] = useState<'add' | 'edit' | 'view' | 'delete'>('add');
  const [searchBorrower, setSearchBorrower] = useState('');
  const [searchBank, setSearchBank] = useState('');
  const [searchLoanType, setSearchLoanType] = useState('');
  const [searchStatus, setSearchStatus] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [formData, setFormData] = useState({
    borrower: '',
    bank: '',
    loanType: '',
    loanReference: '',
    signingDate: null as Date | null,
    availabilityExpiryDate: null as Date | null,
    hedgeCeilingAmount: '',
    approvedLimit: '',
    status: 'Active',
  });

  const borrowers = ['Borrower A', 'Borrower B', 'Borrower C']; // TODO: Fetch from API
  const banks = ['Bank A', 'Bank B', 'Bank C']; // TODO: Fetch from API
  const loanTypes = ['STL', 'MTL', 'LTL']; // TODO: Fetch from API and filter by selected bank
  const statuses = ['Active', 'Closed', 'Cancelled'];

  useEffect(() => {
    // TODO: Fetch loan transactions from API
    const mockLoans: LoanTransaction[] = [
      {
        loanId: 'LN001',
        borrower: 'Borrower A',
        bank: 'Bank A',
        loanType: 'STL',
        loanReference: 'REF001',
        signingDate: new Date('2024-01-01'),
        availabilityExpiryDate: new Date('2024-12-31'),
        hedgeCeilingAmount: 1000000.00,
        approvedLimit: 500000.00,
        status: 'Active',
        rateOfInterest: 5.5,
        gracePeriod: 3,
        principalRepaymentFrequency: 'Monthly',
        interestRepaymentFrequency: 'Monthly',
        arrangementFee: 1.0,
        creationDate: new Date('2024-01-01'),
        lastUpdatedDate: new Date('2024-01-01'),
        createdBy: 'Admin',
        lastUpdatedBy: 'Admin',
      },
      {
        loanId: 'LN002',
        borrower: 'Borrower B',
        bank: 'Bank B',
        loanType: 'LTL',
        loanReference: 'REF002',
        signingDate: new Date('2024-02-01'),
        availabilityExpiryDate: new Date('2024-11-30'),
        hedgeCeilingAmount: 2000000.00,
        approvedLimit: 1500000.00,
        status: 'Closed',
        rateOfInterest: 6.0,
        gracePeriod: 6,
        principalRepaymentFrequency: 'Quarterly',
        interestRepaymentFrequency: 'Quarterly',
        arrangementFee: 1.2,
        creationDate: new Date('2024-02-01'),
        lastUpdatedDate: new Date('2024-02-01'),
        createdBy: 'Admin',
        lastUpdatedBy: 'Admin',
      },
      {
        loanId: 'LN003',
        borrower: 'Borrower C',
        bank: 'Bank C',
        loanType: 'MTL',
        loanReference: 'REF003',
        signingDate: new Date('2024-03-01'),
        availabilityExpiryDate: new Date('2024-10-31'),
        hedgeCeilingAmount: 3000000.00,
        approvedLimit: 2500000.00,
        status: 'Active',
        rateOfInterest: 5.8,
        gracePeriod: 4,
        principalRepaymentFrequency: 'Semi-Annual',
        interestRepaymentFrequency: 'Semi-Annual',
        arrangementFee: 1.1,
        creationDate: new Date('2024-03-01'),
        lastUpdatedDate: new Date('2024-03-01'),
        createdBy: 'Admin',
        lastUpdatedBy: 'Admin',
      },
      {
        loanId: 'LN004',
        borrower: 'Borrower A',
        bank: 'Bank B',
        loanType: 'STL',
        loanReference: 'REF004',
        signingDate: new Date('2024-04-01'),
        availabilityExpiryDate: new Date('2024-09-30'),
        hedgeCeilingAmount: 1200000.00,
        approvedLimit: 800000.00,
        status: 'Cancelled',
        rateOfInterest: 5.2,
        gracePeriod: 2,
        principalRepaymentFrequency: 'Monthly',
        interestRepaymentFrequency: 'Monthly',
        arrangementFee: 1.3,
        creationDate: new Date('2024-04-01'),
        lastUpdatedDate: new Date('2024-04-01'),
        createdBy: 'Admin',
        lastUpdatedBy: 'Admin',
      },
      {
        loanId: 'LN005',
        borrower: 'Borrower B',
        bank: 'Bank C',
        loanType: 'LTL',
        loanReference: 'REF005',
        signingDate: new Date('2024-05-01'),
        availabilityExpiryDate: new Date('2024-08-31'),
        hedgeCeilingAmount: 1800000.00,
        approvedLimit: 1200000.00,
        status: 'Active',
        rateOfInterest: 5.7,
        gracePeriod: 5,
        principalRepaymentFrequency: 'Quarterly',
        interestRepaymentFrequency: 'Quarterly',
        arrangementFee: 1.4,
        creationDate: new Date('2024-05-01'),
        lastUpdatedDate: new Date('2024-05-01'),
        createdBy: 'Admin',
        lastUpdatedBy: 'Admin',
      },
      {
        loanId: 'LN006',
        borrower: 'Borrower C',
        bank: 'Bank A',
        loanType: 'MTL',
        loanReference: 'REF006',
        signingDate: new Date('2024-06-01'),
        availabilityExpiryDate: new Date('2024-07-31'),
        hedgeCeilingAmount: 2200000.00,
        approvedLimit: 1700000.00,
        status: 'Closed',
        rateOfInterest: 5.9,
        gracePeriod: 3,
        principalRepaymentFrequency: 'Semi-Annual',
        interestRepaymentFrequency: 'Semi-Annual',
        arrangementFee: 1.5,
        creationDate: new Date('2024-06-01'),
        lastUpdatedDate: new Date('2024-06-01'),
        createdBy: 'Admin',
        lastUpdatedBy: 'Admin',
      },
    ];
    setLoanTransactions(mockLoans);
  }, []);

  const handleOpenDialog = (type: 'add' | 'edit' | 'view' | 'delete', loan?: LoanTransaction) => {
    setDialogType(type);
    if (loan) {
      setSelectedLoan(loan);
      setFormData({
        borrower: loan.borrower,
        bank: loan.bank,
        loanType: loan.loanType,
        loanReference: loan.loanReference,
        signingDate: loan.signingDate,
        availabilityExpiryDate: loan.availabilityExpiryDate,
        hedgeCeilingAmount: loan.hedgeCeilingAmount.toString(),
        approvedLimit: loan.approvedLimit.toString(),
        status: loan.status,
      });
    } else {
      setFormData({
        borrower: '',
        bank: '',
        loanType: '',
        loanReference: '',
        signingDate: null,
        availabilityExpiryDate: null,
        hedgeCeilingAmount: '',
        approvedLimit: '',
        status: 'Active',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedLoan(null);
  };

  const handleSave = () => {
    // TODO: Implement save logic
    handleCloseDialog();
  };

  const handleDelete = () => {
    if (selectedLoan) {
      // TODO: Implement delete logic with validation
      handleCloseDialog();
    }
  };

  const handleGenerateSchedule = () => {
    // TODO: Implement repayment schedule generation
  };

  const [filterBorrower, setFilterBorrower] = useState('');
  const [filterBank, setFilterBank] = useState('');
  const [filterLoanType, setFilterLoanType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const filteredLoans = loanTransactions.filter(loan => {
    const matchesBorrower = !filterBorrower || loan.borrower === filterBorrower;
    const matchesBank = !filterBank || loan.bank === filterBank;
    const matchesLoanType = !filterLoanType || loan.loanType === filterLoanType;
    const matchesStatus = !filterStatus || loan.status === filterStatus;
    return matchesBorrower && matchesBank && matchesLoanType && matchesStatus;
  });

  const paginatedLoans = filteredLoans.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" sx={{ color: '#374151', fontWeight: 600 }}>
            Loan Transaction
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1, gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<TableChartIcon />}
              sx={{
                borderRadius: 2,
                fontWeight: 500,
                bgcolor: '#fff',
                color: '#1E293B',
                borderColor: '#E5E7EB',
                textTransform: 'none',
                boxShadow: 'none',
                px: 2.5,
                '&:hover': { bgcolor: '#F3F4F6', borderColor: '#6366F1' }
              }}
            >
              Export to Excel
            </Button>
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              sx={{
                borderRadius: 2,
                fontWeight: 500,
                bgcolor: '#fff',
                color: '#1E293B',
                borderColor: '#E5E7EB',
                textTransform: 'none',
                boxShadow: 'none',
                px: 2.5,
                '&:hover': { bgcolor: '#F3F4F6', borderColor: '#6366F1' }
              }}
            >
              Print
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('add')}
              color="primary"
              sx={{
                fontWeight: 600,
                px: 4,
                borderRadius: 2,
                boxShadow: '0 1px 2px 0 rgba(59,130,246,0.08)',
                textTransform: 'none',
                '&:hover': { bgcolor: '#2563EB' }
              }}
            >
              Add New Loan
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            alignItems: 'center',
            gap: 2,
            mb: 3,
            p: 2,
            bgcolor: '#F9FAFB',
            border: '1px solid #E5E7EB',
            borderRadius: 3,
            boxShadow: '0 1px 3px 0 rgba(0,0,0,0.03)',
          }}
        >
          <FormControl sx={{ minWidth: 180, maxWidth: 220, bgcolor: '#fff', borderRadius: 2 }} size="small">
            <InputLabel>Borrower</InputLabel>
            <Select
              value={filterBorrower}
              label="Borrower"
              onChange={(e) => setFilterBorrower(e.target.value)}
              sx={{ height: 40 }}
            >
              <MenuItem value="">All Borrowers</MenuItem>
              {borrowers.map((borrower) => (
                <MenuItem key={borrower} value={borrower}>{borrower}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 180, maxWidth: 220, bgcolor: '#fff', borderRadius: 2 }} size="small">
            <InputLabel>Bank</InputLabel>
            <Select
              value={filterBank}
              label="Bank"
              onChange={(e) => setFilterBank(e.target.value)}
              sx={{ height: 40 }}
            >
              <MenuItem value="">All Banks</MenuItem>
              {banks.map((bank) => (
                <MenuItem key={bank} value={bank}>{bank}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 180, maxWidth: 220, bgcolor: '#fff', borderRadius: 2 }} size="small">
            <InputLabel>Loan Type</InputLabel>
            <Select
              value={filterLoanType}
              label="Loan Type"
              onChange={(e) => setFilterLoanType(e.target.value)}
              sx={{ height: 40 }}
            >
              <MenuItem value="">All Loan Types</MenuItem>
              {loanTypes.map((type) => (
                <MenuItem key={type} value={type}>{type}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 180, maxWidth: 220, bgcolor: '#fff', borderRadius: 2 }} size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={filterStatus}
              label="Status"
              onChange={(e) => setFilterStatus(e.target.value)}
              sx={{ height: 40 }}
            >
              <MenuItem value="">All Statuses</MenuItem>
              {statuses.map((status) => (
                <MenuItem key={status} value={status}>{status}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="contained"
            color="primary"
            startIcon={<SearchIcon />}
            size="medium"
            sx={{ fontWeight: 500, px: 3, borderRadius: 2, boxShadow: 'none', textTransform: 'none', height: 40, minWidth: 100 }}
          >
            Search
          </Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={<ClearIcon />}
            onClick={() => { setFilterBorrower(''); setFilterBank(''); setFilterLoanType(''); setFilterStatus(''); }}
            size="medium"
            sx={{ fontWeight: 500, px: 3, borderRadius: 2, boxShadow: 'none', textTransform: 'none', height: 40, minWidth: 100 }}
          >
            Reset
          </Button>
        </Box>

        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 3, boxShadow: '0 2px 8px 0 rgba(99,102,241,0.06)', background: '#fff', mt: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, color: '#1E293B', bgcolor: '#F9FAFB', fontSize: '1.05rem', py: 0.5 }}>Loan ID</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1E293B', bgcolor: '#F9FAFB', fontSize: '1.05rem', py: 0.5 }}>Borrower</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1E293B', bgcolor: '#F9FAFB', fontSize: '1.05rem', py: 0.5 }}>Bank</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1E293B', bgcolor: '#F9FAFB', fontSize: '1.05rem', py: 0.5 }}>Loan Type</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1E293B', bgcolor: '#F9FAFB', fontSize: '1.05rem', py: 0.5 }}>Loan Reference</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1E293B', bgcolor: '#F9FAFB', fontSize: '1.05rem', py: 0.5 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1E293B', bgcolor: '#F9FAFB', fontSize: '1.05rem', py: 0.5 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedLoans.map((loan, idx) => (
                <TableRow
                  key={loan.loanId}
                  hover
                  sx={{
                    '&:hover': { bgcolor: '#F5F6FF' },
                    borderBottom: idx !== paginatedLoans.length - 1 ? '1px solid #E5E7EB' : 'none',
                    transition: 'background 0.2s',
                    height: 36
                  }}
                >
                  <TableCell sx={{ color: '#374151', py: 0.5 }}>{loan.loanId}</TableCell>
                  <TableCell sx={{ color: '#374151', py: 0.5 }}>{loan.borrower}</TableCell>
                  <TableCell sx={{ color: '#374151', py: 0.5 }}>{loan.bank}</TableCell>
                  <TableCell sx={{ color: '#374151', py: 0.5 }}>{loan.loanType}</TableCell>
                  <TableCell sx={{ color: '#374151', py: 0.5 }}>{loan.loanReference}</TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    <Chip
                      label={loan.status}
                      color={
                        loan.status === 'Active' ? 'success' :
                        loan.status === 'Closed' ? 'default' : 'error'
                      }
                      size="small"
                      sx={{
                        fontWeight: 500,
                        '&.MuiChip-colorSuccess': {
                          bgcolor: '#ECFDF5',
                          color: '#059669',
                        },
                        '&.MuiChip-colorError': {
                          bgcolor: '#FEF2F2',
                          color: '#DC2626',
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    <IconButton 
                      onClick={() => handleOpenDialog('view', loan)}
                      sx={{ color: '#6366F1' }}
                    >
                      <VisibilityIcon />
                    </IconButton>
                    <IconButton 
                      onClick={() => handleOpenDialog('edit', loan)}
                      sx={{ color: '#6366F1' }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton 
                      onClick={() => handleOpenDialog('delete', loan)}
                      sx={{ color: '#EF4444' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                    <IconButton 
                      onClick={handleGenerateSchedule}
                      sx={{ color: '#6366F1' }}
                    >
                      <CalendarMonthIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={filteredLoans.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </TableContainer>

        <Dialog 
          open={openDialog} 
          onClose={handleCloseDialog} 
          maxWidth="md" 
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
            }
          }}
        >
          <DialogTitle sx={{ 
            color: '#374151',
            fontWeight: 600,
            borderBottom: '1px solid #E5E7EB',
            pb: 2
          }}>
            {dialogType === 'add' && 'Add New Loan'}
            {dialogType === 'edit' && 'Edit Loan'}
            {dialogType === 'view' && 'View Loan Details'}
            {dialogType === 'delete' && 'Delete Loan'}
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {dialogType !== 'delete' ? (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Loan ID"
                    value={selectedLoan?.loanId || 'Auto-generated'}
                    fullWidth
                    disabled
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#E5E7EB',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Borrower</InputLabel>
                    <Select
                      value={formData.borrower}
                      onChange={(e) => setFormData({ ...formData, borrower: e.target.value })}
                      label="Borrower"
                      disabled={dialogType === 'view'}
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#E5E7EB',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#6366F1',
                        },
                      }}
                    >
                      {borrowers.map((borrower) => (
                        <MenuItem key={borrower} value={borrower}>{borrower}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Bank</InputLabel>
                    <Select
                      value={formData.bank}
                      onChange={(e) => setFormData({ ...formData, bank: e.target.value })}
                      label="Bank"
                      disabled={dialogType === 'view'}
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#E5E7EB',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#6366F1',
                        },
                      }}
                    >
                      {banks.map((bank) => (
                        <MenuItem key={bank} value={bank}>{bank}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Loan Type</InputLabel>
                    <Select
                      value={formData.loanType}
                      onChange={(e) => setFormData({ ...formData, loanType: e.target.value })}
                      label="Loan Type"
                      disabled={dialogType === 'view'}
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#E5E7EB',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#6366F1',
                        },
                      }}
                    >
                      {loanTypes.map((type) => (
                        <MenuItem key={type} value={type}>{type}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Loan Reference"
                    value={formData.loanReference}
                    onChange={(e) => setFormData({ ...formData, loanReference: e.target.value })}
                    fullWidth
                    disabled={dialogType === 'view'}
                    inputProps={{ maxLength: 30 }}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#E5E7EB',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#6366F1',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="Signing Date"
                    value={formData.signingDate}
                    onChange={(date) => setFormData({ ...formData, signingDate: date })}
                    disabled={dialogType === 'view'}
                    sx={{
                      width: '100%',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#E5E7EB',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#6366F1',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="Availability Expiry Date"
                    value={formData.availabilityExpiryDate}
                    onChange={(date) => setFormData({ ...formData, availabilityExpiryDate: date })}
                    disabled={dialogType === 'view'}
                    sx={{
                      width: '100%',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#E5E7EB',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#6366F1',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Hedge Ceiling Amount"
                    type="number"
                    value={formData.hedgeCeilingAmount}
                    onChange={(e) => setFormData({ ...formData, hedgeCeilingAmount: e.target.value })}
                    fullWidth
                    disabled={dialogType === 'view'}
                    inputProps={{ step: "0.01" }}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#E5E7EB',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#6366F1',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Approved Limit"
                    type="number"
                    value={formData.approvedLimit}
                    onChange={(e) => setFormData({ ...formData, approvedLimit: e.target.value })}
                    fullWidth
                    disabled={dialogType === 'view'}
                    inputProps={{ step: "0.01" }}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#E5E7EB',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#6366F1',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      label="Status"
                      disabled={dialogType === 'view'}
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#E5E7EB',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#6366F1',
                        },
                      }}
                    >
                      {statuses.map((status) => (
                        <MenuItem key={status} value={status}>{status}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {selectedLoan && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" sx={{ color: '#374151', fontWeight: 600, mb: 1 }}>
                        Loan Type Details
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Rate of Interest"
                        value={selectedLoan.rateOfInterest}
                        fullWidth
                        disabled
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#E5E7EB',
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Grace Period"
                        value={selectedLoan.gracePeriod}
                        fullWidth
                        disabled
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#E5E7EB',
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Principal Repayment Frequency"
                        value={selectedLoan.principalRepaymentFrequency}
                        fullWidth
                        disabled
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#E5E7EB',
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Interest Repayment Frequency"
                        value={selectedLoan.interestRepaymentFrequency}
                        fullWidth
                        disabled
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#E5E7EB',
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Arrangement Fee"
                        value={selectedLoan.arrangementFee}
                        fullWidth
                        disabled
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#E5E7EB',
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" sx={{ color: '#374151', fontWeight: 600, mb: 1 }}>
                        Audit Information
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Creation Date"
                        value={selectedLoan.creationDate.toLocaleDateString()}
                        fullWidth
                        disabled
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#E5E7EB',
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Last Updated Date"
                        value={selectedLoan.lastUpdatedDate.toLocaleDateString()}
                        fullWidth
                        disabled
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#E5E7EB',
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Created By"
                        value={selectedLoan.createdBy}
                        fullWidth
                        disabled
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#E5E7EB',
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Last Updated By"
                        value={selectedLoan.lastUpdatedBy}
                        fullWidth
                        disabled
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#E5E7EB',
                          },
                        }}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            ) : (
              <Typography sx={{ color: '#374151' }}>
                Are you sure you want to delete this loan? This action cannot be undone.
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, borderTop: '1px solid #E5E7EB' }}>
            <Button 
              onClick={handleCloseDialog}
              sx={{
                color: '#374151',
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': {
                  bgcolor: '#F5F6FF',
                },
              }}
            >
              Cancel
            </Button>
            {dialogType === 'delete' ? (
              <Button 
                onClick={handleDelete} 
                color="error"
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                Delete
              </Button>
            ) : (
              <Button 
                onClick={handleSave} 
                variant="contained"
                sx={{
                  bgcolor: '#6366F1',
                  '&:hover': {
                    bgcolor: '#4F46E5',
                  },
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                Save
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default LoanTransaction; 